import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Edit, Mail, Trash } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { NotificationSubscriptionButton } from "@/components/notifications/NotificationSubscriptionButton";
import { FavoriteButton } from "@/components/favorites/FavoriteButton";
import { BackButton } from "@/components/ui/BackButton";
import { useJobDetail } from "@/hooks/jobs/useJobDetail";
import { toast } from "@/hooks/use-toast";
import { SendEmailModal } from "@/components/email/SendEmailModal";
import { SuperAdminReanalyzeButton } from "@/components/admin/SuperAdminReanalyzeButton";

import { supabase } from "@/integrations/supabase/client";
import { JobFormData } from "./types/jobTypes";
import { useTranslation } from "react-i18next";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel
} from "@/components/ui/alert-dialog";

interface JobDetailHeaderProps {
    jobId: string | undefined;
    refreshData?: () => void;
    onEdit?: () => void;
    isEditing?: boolean;
}

export const JobDetailHeader = ({ jobId, refreshData, onEdit, isEditing }: JobDetailHeaderProps) => {
    const { t } = useTranslation();
    const { job, refetch } = useJobDetail(jobId);
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const navigate = useNavigate();

    // Helper function to normalize language requirements to an array
    const normalizeLanguageRequirements = (langReq: string | string[] | null | undefined): string[] => {
        if (!langReq) return [];
        if (Array.isArray(langReq)) return langReq;
        return [langReq];
    };

    // Helper to translate a language name
    const translateLanguageName = (language: string): string => {
        const lowercase = language.toLowerCase();
        const supportedLanguages = ['english', 'dutch', 'french', 'german', 'spanish', 'italian', 'other'];

        if (supportedLanguages.includes(lowercase)) {
            return t(`jobs.formWizard.languages.${lowercase}`, language);
        }

        return language;
    };

    const handleUpdateJob = async (updatedJob: JobFormData) => {
        if (!jobId) {
            console.error("No job ID provided for update");
            toast({
                title: t("common.error", "Error"),
                description: t("jobs.detail.noJobId", "Cannot update job: No job ID provided"),
                variant: "destructive",
            });
            return false;
        }

        setIsSaving(true);

        try {
            // Process the arrays from comma-separated strings
            const skillsArray = updatedJob.skills
                ? updatedJob.skills.split(',').map(skill => skill.trim()).filter(Boolean)
                : [];

            const hardSkillsArray = updatedJob.hardSkills
                ? updatedJob.hardSkills.split(',').map(skill => skill.trim()).filter(Boolean)
                : [];

            const softSkillsArray = updatedJob.softSkills
                ? updatedJob.softSkills.split(',').map(skill => skill.trim()).filter(Boolean)
                : [];

            const cultureTagsArray = updatedJob.cultureTags
                ? updatedJob.cultureTags.split(',').map(tag => tag.trim()).filter(Boolean)
                : [];

            // Normalize language requirements
            const languageRequirementsArray = normalizeLanguageRequirements(updatedJob.languageRequirements);

            // Normalize null/undefined values for the database
            const endDate = updatedJob.endDate && typeof updatedJob.endDate === 'object'
                ? null
                : updatedJob.endDate || null;

            const extraLang = updatedJob.extraLang && typeof updatedJob.extraLang === 'object'
                ? null
                : updatedJob.extraLang || null;

            // First get the current job data to compare for history logging
            const { data: currentJob, error: fetchError } = await supabase
                .from('jobs')
                .select('*')
                .eq('id', jobId)
                .single();

            if (fetchError) {
                console.error("Error fetching current job data:", fetchError);
                throw fetchError;
            }

            // Clean up potential undefined values for database
            const jobUpdateData = {
                title: updatedJob.title || null,
                company_id: updatedJob.companyId || null,
                description: updatedJob.description || null,
                ai_summary: updatedJob.aiSummary || null,
                skills: skillsArray.length > 0 ? skillsArray : null,
                contact_id: updatedJob.contactId || null,
                employment_type: updatedJob.employmentType || null,
                contract_duration: updatedJob.contractDuration || null,
                start_date: updatedJob.startDate || null,
                end_date: endDate,
                seniority_level: updatedJob.seniorityLevel || null,
                budget: updatedJob.budget || null,
                budget_freelance: updatedJob.budgetFreelance || null,
                location_type: updatedJob.locationType || null,
                workplace_breakdown: updatedJob.workplaceBreakdown || null,
                hard_skills: hardSkillsArray.length > 0 ? hardSkillsArray : null,
                soft_skills: softSkillsArray.length > 0 ? softSkillsArray : null,
                education_level: updatedJob.educationLevel || null,
                years_of_experience: updatedJob.yearsOfExperience || null,
                culture_tags: cultureTagsArray.length > 0 ? cultureTagsArray : null,
                application_deadline: updatedJob.applicationDeadline || null,
                job_posting_date: updatedJob.jobPostingDate || null,
                source: updatedJob.source || null,
                assigned_recruiter: updatedJob.assignedRecruiter || null,
                employment_classification: updatedJob.employmentClassification || null,
                status: updatedJob.status || null,
                closing_reason: updatedJob.closing_reason || null
            };

            const { error: updateError } = await supabase
                .from('jobs')
                .update(jobUpdateData)
                .eq('id', jobId);


            if (updateError) {
                console.error("Error from Supabase:", updateError);
                toast({
                    title: t("common.error", "Error"),
                    description: t("jobs.detail.updateFailed", "Failed to update job offer: ") + updateError.message,
                    variant: "destructive",
                });
                return false;
            }

            // When job is closed (won or lost), close all open matches and unpublish
            if (updatedJob.status === 'closed/won' || updatedJob.status === 'closed/lost') {
                await supabase.rpc('set_match_application_closed', { p_job_id: jobId });
                await supabase
                    .from("jobs")
                    .update({ published: false, publishdate: null })
                    .eq("id", jobId);
                await supabase
                    .from("jobs_public")
                    .delete()
                    .eq("job_id", jobId);
            }

            //console.log('Triggering AI analysis for job ID:', jobId);

            const { data: analysisData, error: analysisError } = await supabase.functions.invoke(
                'ai-processing?command=push-new-analysis',
                {
                    body: {
                        entityId: jobId,
                        entityType: "job",
                        analysisType: "analyse-job-offer",
                        actionOnComplete: "update-job",
                    }
                }
            );

            if (analysisError) {
                console.error("Error analyzing job offer:", analysisError);
            }

            // Only now refresh the job data after successful update
            if (refreshData) {
                //console.log("Triggering manual refresh after successful save");
                refreshData();
            } else if (refetch) {
                //console.log("Calling refetch after successful save");
                await refetch();
            }

            toast({
                title: t("common.success", "Success"),
                description: t("jobs.detail.updateSuccess", "Job offer updated successfully"),
            });

            return true;
        } catch (error) {
            console.error("Error updating job:", error);
            toast({
                title: t("common.error", "Error"),
                description: t("jobs.detail.updateFailed", "Failed to update job offer: ") + (error instanceof Error ? error.message : t("common.unknownError", "Unknown error")),
                variant: "destructive",
            });
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteJob = async () => {
        if (!jobId) return;

        setIsDeleting(true);

        try {
            // Delete associated matches first to maintain referential integrity
            const { error: matchesError } = await supabase
                .from('matches')
                .delete()
                .eq('job_id', jobId);

            if (matchesError) {
                console.error("Error deleting job matches:", matchesError);
            }

            // Delete job comments
            const { error: commentsError } = await supabase
                .from('job_comments')
                .delete()
                .eq('job_id', jobId);

            if (commentsError) {
                console.error("Error deleting job comments:", commentsError);
            }

            // Finally delete the job itself
            const { error } = await supabase
                .from('jobs')
                .delete()
                .eq('id', jobId);

            if (error) {
                console.error("Error deleting job:", error);
                toast({
                    title: t("common.error", "Error"),
                    description: t("jobs.detail.deleteError", "Failed to delete job offer: ") + error.message,
                    variant: "destructive",
                });
                return;
            }

            toast({
                title: t("common.success", "Success"),
                description: t("jobs.detail.deleteSuccess", "Job offer deleted successfully"),
            });

            // Redirect to the jobs list page after successful deletion
            navigate('/jobs');

        } catch (error) {
            console.error("Error in job deletion:", error);
            toast({
                title: t("common.error", "Error"),
                description: t("jobs.detail.deleteError", "Failed to delete job offer"),
                variant: "destructive",
            });
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    return (
        <>
            <div className="flex justify-between items-center mb-6">
                <BackButton 
                    to="/jobs" 
                    label={t("jobs.detail.backToJobs", "Back")} 
                />
                <div className="flex gap-2 pt-2">
                    <FavoriteButton
                        entityType="job"
                        entityId={jobId || ''}
                        entityName={job?.title || ''}
                        variant="outline"
                        size="icon"
                    />
                    <NotificationSubscriptionButton
                        entityType="job"
                        entityId={jobId || ''}
                        entityName={job?.title || ''}
                        variant="outline"
                        size="icon"
                    />
                    <SuperAdminReanalyzeButton entityType="job" entityId={jobId || ''} />
                    <Button
                        variant="outline"
                        size="icon"
                        className="flex items-center justify-center"
                        onClick={() => setShowEmailModal(true)}
                        disabled={!job}
                        title={t("jobs.detail.sendByEmail", "Send by Email")}
                    >
                        <Mail className="h-4 w-4" />
                    </Button>
                    {!isEditing ? (
                        <Button
                            variant="outline"
                            size="icon"
                            className="flex items-center justify-center"
                            onClick={onEdit}
                            disabled={isSaving}
                            title={isSaving ? t("common.saving", "Saving...") : t("common.edit", "Edit")}
                        >
                            <Edit className="h-4 w-4" />
                        </Button>
                    ) : null}
                    <Button
                        variant="outline"
                        size="icon"
                        className="flex items-center justify-center text-red-600 hover:text-red-700"
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={isDeleting}
                        title={isDeleting ? t("common.deleting", "Deleting...") : t("common.delete", "Delete")}
                    >
                        <Trash className="h-4 w-4" />
                    </Button>
                </div>
            </div>



            {/* Delete Confirmation Dialog */}
            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent className="sm:max-w-[425px]">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("jobs.detail.confirmDelete", "Confirm Deletion")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("jobs.detail.deleteWarning", "Are you sure you want to delete this job offer? This action cannot be undone and will remove all associated matches and comments.")}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel", "Cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteJob}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            disabled={isDeleting}
                        >
                            {isDeleting ? t("common.deleting", "Deleting...") : t("common.delete", "Delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Send Email Modal */}
            {job && (
                <SendEmailModal
                    open={showEmailModal}
                    onClose={() => setShowEmailModal(false)}
                    job={{
                        id: job.id,
                        title: job.title,
                        description: job.description || "",
                        publicDescription: (job as any).JobDesc_Public || "",
                        skills: job.skills || [],
                        company: {
                            id: job.company?.id || "",
                            name: job.company?.name || "",
                        },
                    }}
                    defaultTemplate="job_offer"
                />
            )}
        </>
    );
};
